import { hasUnfilledNoteSlots } from './noteTemplates.js';
export type WellnessAnswer = Record<string, string>;

export const WELLNESS_FORM_VERSION = '2.7-conditional-field-visibility';
export interface WellnessContext {
  /** Server-resolved address, never accepted from an editable answer. */
  originalAddress?: string | null;
  originalAddressCaseId?: string | null;
}

export interface WellnessField {
  name: string;
  portal: string;
  label: string;
  kind: 'text' | 'name' | 'case' | 'alien' | 'officer' | 'choice' | 'textarea' | 'address';
  required?: boolean;
  allowedValues?: readonly string[];
  derivedFrom?: string;
}

export const wellnessFields: readonly WellnessField[] = [
  { name: 'nonPliCase', portal: 'CASE_ID', label: 'Non-PII case number', kind: 'case', required: true },
  { name: 'alienNumberPrimary', portal: 'A_NUMBER', label: 'A-Number', kind: 'alien', required: true },
  { name: 'officerFirstName', portal: 'FIRST_NAME', label: "Case officer's first name", kind: 'officer', required: true },
  { name: 'officerLastName', portal: 'LAST_NAME', label: "Case officer's last name", kind: 'officer', required: true },
  { name: 'uacAlienNumber', portal: 'UAC_A_NUMBER', label: "UAC's A-Number", kind: 'alien', required: true, derivedFrom: 'alienNumberPrimary' },
  { name: 'uacFirstName', portal: 'UAC_FIRST_NAME', label: "UAC's first name", kind: 'name', required: true },
  { name: 'uacLastName', portal: 'UAC_LAST_NAME', label: "UAC's last name", kind: 'name', required: true },
  { name: 'vendorContact', portal: 'IS_UNABLE_TO_CONTACT', label: 'Unable to contact sponsor and UAC', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'returnInfo', portal: 'IS_UAC_RETURNED_TO_COUNTRY', label: 'UAC returned to country or departed the United States', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'siteVisit', portal: 'IS_ONSITE_VERIFICATION_COMPLETED', label: 'On-site verification completed', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'notPwsRequirement', portal: 'IS_ADDRESS_VERIFIED_CODED_LETTER', label: 'Address verified through coded letter', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'locatedAtAddress', portal: 'UAC_LOCATED_AT_ADDRESS', label: 'UAC located at address', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'locationComments', portal: 'UAC_LOCATED_AT_ADDRESS_DESC', label: 'Address where the UAC was located', kind: 'address' },
  { name: 'safeAtVisit', portal: 'IS_UAC_SAFE', label: 'UAC safe', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'safetyDetermination', portal: 'IS_UAC_SAFE_DESC', label: 'Safety determination note', kind: 'textarea' },
  { name: 'identifiedSponsor', portal: 'IS_UAC_LIVING_WITH_LISTED_SPONSOR', label: 'Living with listed sponsor', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'sponsorComments', portal: 'IS_UAC_LIVING_WITH_LISTED_SPONSOR_REASON', label: 'Reason the UAC is not living with the listed sponsor', kind: 'textarea' },
  { name: 'enrolledSchool', portal: 'IS_UAC_ENROLLED_SCHOOL', label: 'Enrolled in school', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'schoolComments', portal: 'EDUCATION_DETAILS', label: 'Education details', kind: 'textarea' },
  { name: 'working', portal: 'IS_UAC_WORKING', label: 'UAC working', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'employmentComments', portal: 'IS_UAC_WORKING_DESC', label: 'Employment note', kind: 'textarea' },
  { name: 'abuseTrafficking', portal: 'SIGNS_ABUSE_TRAFFICKING', label: 'Signs of abuse or trafficking', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'abuseComments', portal: 'SIGNS_ABUSE_TRAFFICKING_DESC', label: 'Abuse or trafficking note', kind: 'textarea' },
  { name: 'lawEnforcement', portal: 'SPONSOR_ARRESTED', label: 'Sponsor arrested', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'childProtectiveServices', portal: 'UAC_REMOVED_FOR_SAFETY', label: 'UAC removed for safety', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'rjcNotification', portal: 'HSI_ERO_NOTIFIED', label: 'HSI or ERO notified', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'eoirProceeding', portal: 'IS_UAC_INVOLVED_IMMIGRATION_PROCEEDINGS', label: 'Immigration proceedings found', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'eoirHearing', portal: 'HAS_COURT_DATE_ASSIGNED_UAC', label: 'Court date assigned', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'eoirAddressMatch', portal: 'DOES_UAC_ADDRESS_CORRESPOND_COURT', label: 'Address corresponds to court', kind: 'choice', allowedValues: ['Yes', 'No'] },
  { name: 'confirmedAddress', portal: 'UAC_RECEIVE_ADDRESS', label: 'Confirmed mail address', kind: 'address' },
  { name: 'finalDetermination', portal: 'COMPLETED', label: 'Record completed', kind: 'choice', allowedValues: ['TRUE', 'FALSE'] },
];

const workflowFields = wellnessFields.slice(9).map((field) => field.name);
const requiredWhenReached = [
  'siteVisit', 'notPwsRequirement', 'locatedAtAddress', 'safeAtVisit',
  'identifiedSponsor', 'enrolledSchool', 'working', 'abuseTrafficking',
  'lawEnforcement', 'childProtectiveServices', 'rjcNotification',
  'eoirProceeding', 'eoirHearing', 'eoirAddressMatch',
] as const;

export const wellnessSteps = [
  { title: 'Case', fields: wellnessFields.slice(0, 7) },
  { title: 'Contact', fields: wellnessFields.slice(7, 9) },
  { title: 'Wellness', fields: wellnessFields.slice(9, 17) },
  { title: 'School and safety', fields: wellnessFields.slice(17, 25) },
  { title: 'Legal and outcome', fields: wellnessFields.slice(25) },
] as const;

const wellnessQuestionNumbers = new Map(
  wellnessFields.map((field, index) => [field.name, index + 1]),
);

export function wellnessQuestionNumber(field: WellnessField | string): number {
  const name = typeof field === 'string' ? field : field.name;
  return wellnessQuestionNumbers.get(name) ?? 0;
}

function clean(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const postalAddressPattern = /^\d{1,6}\s+[^,\r\n]+,\s*[a-z][a-z .'-]+,\s*[a-z]{2}\s+\d{5}(?:-\d{4})?$/iu;
const personNamePattern = /^[\p{L}\p{M}][\p{L}\p{M} .,'’\-]{0,99}$/u;

export const MASKED_CASE_OFFICER_NAME = '•••••••• ••••••••';
export const PORTAL_ADDRESS_EXAMPLE = '123 Main St, City, ST 12345';

export function formatWellnessOriginalAddress(input: {
  address: string | null; unit?: string | null; city?: string | null;
  state?: string | null; zip?: string | null;
}): string | null {
  if (!input.address || input.address.includes('\u0000')) return null;
  let address = input.address.trim().replace(/\s*,\s*/gu, ', ')
    .replace(/, ([a-z]{2}), (\d{5}(?:-\d{4})?)$/iu, ', $1 $2');
  if (!address.includes(',') && input.city && input.state && input.zip) {
    address = `${address}, ${input.city.trim()}, ${input.state.trim()} ${input.zip.trim()}`;
  }
  if (input.unit) {
    if (input.unit.includes('\u0000')) return null;
    const comma = address.indexOf(',');
    const street = address.slice(0, comma);
    if (comma > 0 && !street.toLocaleLowerCase().endsWith(input.unit.trim().toLocaleLowerCase())) {
      address = `${street} ${input.unit.trim()}${address.slice(comma)}`;
    }
  }
  return postalAddressPattern.test(address) ? address : null;
}

export function formatNonPiiCaseNumber(source: unknown): string {
  const raw = clean(source);
  if (!raw) return '';
  const digits = raw.replace(/\D/gu, '');
  const includesPrefix = raw.startsWith('14-') || (digits.length > 5 && digits.startsWith('14'));
  const suffix = (includesPrefix ? digits.slice(2) : digits).slice(0, 5);
  return `14-${suffix}`;
}

export function normalizeWellnessAnswers(source: unknown, context: WellnessContext = {}): WellnessAnswer {
  const input = source && typeof source === 'object' ? source as Record<string, unknown> : {};
  const result: WellnessAnswer = {};
  for (const field of wellnessFields) {
    let value = clean(input[field.name]).slice(0, 2_000);
    if (field.kind === 'case') value = formatNonPiiCaseNumber(value);
    if (field.kind === 'alien') value = value.replace(/\D/gu, '').slice(0, 9);
    if (field.kind === 'name') value = value.normalize('NFKC').replace(/\s+/gu, ' ').slice(0, 100);
    if (field.kind === 'officer' && !uuidPattern.test(value)) value = '';
    result[field.name] = value;
  }
  for (const field of wellnessFields) {
    if (field.derivedFrom) result[field.name] = result[field.derivedFrom] ?? '';
  }
  if (result.locatedAtAddress === 'Yes') {
    result.locationComments = context.originalAddressCaseId === result.nonPliCase
      ? clean(context.originalAddress) : '';
  }
  if (result.safeAtVisit === 'Yes') result.safetyDetermination = '';
  if (result.safeAtVisit === 'Yes' || result.identifiedSponsor === 'Yes') result.sponsorComments = '';
  return result;
}

function hasTerminalOutcome(data: WellnessAnswer): boolean {
  return data.vendorContact === 'Yes' || data.returnInfo === 'Yes';
}

export function disabledWellnessFields(data: WellnessAnswer): ReadonlySet<string> {
  return new Set([
    ...hiddenWellnessFields(data),
    ...(hasTerminalOutcome(data) ? workflowFields : []),
  ]);
}

/** Visibility only. All 31 portal fields remain in the stored/export contract. */
export function hiddenWellnessFields(data: WellnessAnswer): ReadonlySet<string> {
  const hidden = new Set(['officerFirstName', 'officerLastName', 'uacAlienNumber']);
  if (data.locatedAtAddress !== 'No') hidden.add('locationComments');
  if (data.safeAtVisit !== 'No') hidden.add('safetyDetermination');
  if (data.safeAtVisit !== 'No' || data.identifiedSponsor !== 'No') hidden.add('sponsorComments');
  return hidden;
}

export function requiredWellnessFields(data: WellnessAnswer): ReadonlySet<string> {
  const required = new Set(wellnessFields.filter((field) => field.required).map((field) => field.name));
  if (!data.vendorContact && !data.returnInfo) {
    required.add('vendorContact');
    required.add('returnInfo');
  } else if (data.vendorContact === 'No' && !data.returnInfo) {
    required.add('returnInfo');
  } else if (data.returnInfo === 'No' && !data.vendorContact) {
    required.add('vendorContact');
  }
  if (data.vendorContact === 'No' && data.returnInfo === 'No') {
    requiredWhenReached.forEach((name) => required.add(name));
    if (data.locatedAtAddress === 'No') required.add('locationComments');
    if (data.safeAtVisit === 'No') required.add('safetyDetermination');
    if (data.safeAtVisit === 'No' && data.identifiedSponsor === 'No') required.add('sponsorComments');
    if (data.enrolledSchool === 'No') required.add('schoolComments');
  }
  return required;
}

export function validateWellnessSubmission(source: unknown, context: WellnessContext = {}): {
  valid: boolean;
  data: WellnessAnswer;
  errors: Record<string, string>;
} {
  const raw = source && typeof source === 'object' ? source as Record<string, unknown> : {};
  const data = normalizeWellnessAnswers(raw, context);
  const errors: Record<string, string> = {};
  const required = requiredWellnessFields(data);
  const disabled = disabledWellnessFields(data);

  if (data.vendorContact === 'Yes' && data.returnInfo === 'Yes') {
    errors.vendorContact = 'Unable to contact and returned to country cannot both be Yes.';
    errors.returnInfo = 'Returned to country and unable to contact cannot both be Yes.';
  }

  for (const field of wellnessFields) {
    const value = data[field.name];
    const rawValue = clean(raw[field.name]);
    if (disabled.has(field.name) && !field.required) continue;
    if (field.kind === 'textarea' && hasUnfilledNoteSlots(value)) {
      errors[field.name] = 'Complete or remove unfinished template details before continuing.';
    }
    if (required.has(field.name) && !value && !field.derivedFrom) {
      errors[field.name] = field.kind === 'officer'
        ? 'An administrator must select the shared case officer before this form can continue.'
        : field.name === 'vendorContact' || field.name === 'returnInfo'
          ? 'Choose Yes or No to establish the correct portal path.'
          : 'This answer is required.';
    }
    if (rawValue && field.kind === 'case' && !/^14-\d{5}$/u.test(rawValue)) {
      errors[field.name] = 'Enter exactly 5 digits after 14-.';
    }
    if (rawValue && field.kind === 'alien' && !field.derivedFrom && !/^\d{9}$/u.test(rawValue)) {
      errors[field.name] = 'Enter exactly 9 digits. Letters and punctuation are not allowed.';
    }
    if (value && field.allowedValues && !field.allowedValues.includes(value)) {
      errors[field.name] = `Choose ${field.allowedValues.join(' or ')}.`;
    }
    if (value && field.kind === 'address' && !postalAddressPattern.test(value)) {
      errors[field.name] = `Use the portal format: ${PORTAL_ADDRESS_EXAMPLE}. ZIP+4 is also accepted.`;
    }
    if (value && field.kind === 'name' && !personNamePattern.test(value)) {
      errors[field.name] = 'Enter a valid name using letters, spaces, apostrophes, periods, commas, or hyphens.';
    }
  }

  if (required.has('locationComments') && !data.locationComments) {
    errors.locationComments = `Enter the updated address in this format: ${PORTAL_ADDRESS_EXAMPLE}.`;
  }
  if (!hasTerminalOutcome(data) && data.locatedAtAddress === 'Yes'
      && (!data.locationComments || !postalAddressPattern.test(data.locationComments))) {
    errors.locatedAtAddress = 'The original case address is unavailable or invalid. Ask an administrator to verify the saved address, then reload this draft.';
  }
  if (data.officerFirstName && data.officerLastName
      && data.officerFirstName !== data.officerLastName) {
    errors.officerFirstName = 'The administrator-selected case officer assignment is inconsistent.';
    errors.officerLastName = 'The administrator-selected case officer assignment is inconsistent.';
  }

  if (hasTerminalOutcome(data)) {
    for (const name of workflowFields) data[name] = '';
  }
  return { valid: Object.keys(errors).length === 0, data, errors };
}

/** Caller supplies the authorized, decrypted submission, including officer names.
 * Do not normalize it again: submitted officer values are names, not draft IDs.
 * This mapping retains hidden fields for the Box/portal export schema. */
export function wellnessPortalValues(submission: WellnessAnswer): Record<string, string> {
  return Object.fromEntries(wellnessFields.map((field) => [field.portal, clean(submission[field.name])]));
}

export function emptyWellnessAnswers(): WellnessAnswer {
  return Object.fromEntries(wellnessFields.map((field) => [field.name, '']));
}
